import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { appsAPI, appPlansAPI } from '../../services/api'
import FeatureListEditor from '../../components/FeatureListEditor'

import { logger } from '@/utils/logger';
export default function AppPlansManagement() {
  const { appId } = useParams()
  const navigate = useNavigate()

  const [app, setApp] = useState(null)
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    features: '{}',
    feature_list: '[]',
    is_active: true,
    is_popular: false,
    order_index: 0
  })

  useEffect(() => {
    loadData()
  }, [appId])

  const loadData = async () => {
    try {
      const [appRes, plansRes] = await Promise.all([
        appsAPI.getById(appId),
        appPlansAPI.getAll(appId)
      ])
      setApp(appRes.data)
      setPlans(plansRes.data)
    } catch (err) {
      setError('Fehler beim Laden der Daten')
      logger.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan)
      setFormData({
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        features: plan.features || '{}',
        feature_list: plan.feature_list || '[]',
        is_active: plan.is_active,
        is_popular: plan.is_popular,
        order_index: plan.order_index
      })
    } else {
      setEditingPlan(null)
      setFormData({
        name: '',
        slug: '',
        description: '',
        price_monthly: '',
        price_yearly: '',
        features: '{}',
        feature_list: '[]',
        is_active: true,
        is_popular: false,
        order_index: plans.length
      })
    }
    setShowModal(true)
    setError('')
    setSuccess('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      const submitData = {
        ...formData,
        app_id: parseInt(appId),
        price_monthly: parseFloat(formData.price_monthly),
        price_yearly: parseFloat(formData.price_yearly),
        order_index: parseInt(formData.order_index)
      }

      if (editingPlan) {
        await appPlansAPI.update(editingPlan.id, submitData)
        setSuccess('Plan erfolgreich aktualisiert')
      } else {
        await appPlansAPI.create(submitData)
        setSuccess('Plan erfolgreich erstellt')
      }

      await loadData()
      setShowModal(false)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern')
      logger.error(err)
    }
  }

  const handleDelete = async (plan) => {
    if (!confirm(`Plan "${plan.name}" wirklich löschen?`)) return

    try {
      await appPlansAPI.delete(plan.id)
      setSuccess('Plan gelöscht')
      await loadData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen')
      setTimeout(() => setError(''), 3000)
    }
  }

  // Feature-List Helper
  const parseFeatureList = (jsonString) => {
    try {
      return JSON.parse(jsonString || '[]')
    } catch {
      return []
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <button
            onClick={() => navigate('/admin/apps')}
            className="wc-btn wc-btn-ghost"
            style={{ padding: 0, marginBottom: 8 }}
          >
            ← Zurück zu Apps
          </button>
          <h1 style={{ fontSize: 24, margin: 0 }}>Pläne für {app?.name}</h1>
          <p className="wc-muted" style={{ fontSize: 14, margin: '4px 0 0' }}>Flexible Preispläne verwalten (Basic, Pro, Ultra, etc.)</p>
        </div>
        <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
          + Neuer Plan
        </button>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      {plans.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ alignItems: 'center', textAlign: 'center', padding: '48px 24px' }}>
          <h3 className="wc-card-title" style={{ margin: 0 }}>Keine Pläne vorhanden</h3>
          <p className="wc-muted" style={{ margin: '4px 0 16px' }}>
            Erstellen Sie Preispläne für diese App (z.B. Basic, Pro, Ultra)
          </p>
          <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
            Ersten Plan erstellen
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="wc-card wc-elev-sm"
              style={{
                gap: 14,
                position: 'relative',
                boxShadow: plan.is_popular ? '0 0 0 1px var(--wc-accent)' : undefined,
              }}
            >
              {plan.is_popular && (
                <span
                  className="wc-tag wc-tag-accent"
                  style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)' }}
                >
                  Meistgewählt
                </span>
              )}

              <div>
                <h3 className="wc-card-title" style={{ margin: 0 }}>{plan.name}</h3>
                {plan.description && <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>{plan.description}</p>}
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 26 }}>{plan.price_monthly}€</span>
                  <span className="wc-muted" style={{ fontSize: 13 }}>/Monat</span>
                </div>
                <div className="wc-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  oder {plan.price_yearly}€ /Jahr
                  <span style={{ color: 'var(--wc-accent-300)', marginLeft: 8 }}>
                    (spare {(plan.price_monthly * 12 - plan.price_yearly).toFixed(2)}€)
                  </span>
                </div>
              </div>

              {plan.feature_list && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {parseFeatureList(plan.feature_list).map((feature, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--wc-accent)', flexShrink: 0, marginTop: 2 }} aria-hidden="true">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                <span className="wc-muted">Sortierung: {plan.order_index}</span>
                <span className={`wc-tag ${plan.is_active ? 'wc-tag-accent' : 'wc-tag-neutral'}`}>
                  {plan.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => handleOpenModal(plan)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                  Bearbeiten
                </button>
                <button
                  onClick={() => handleDelete(plan)}
                  className="wc-btn wc-btn-secondary"
                  style={{ color: '#fca5a5' }}
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="wc-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="wc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>
              {editingPlan ? 'Plan bearbeiten' : 'Neuer Plan'}
            </h3>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>Plan-Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="wc-input"
                    placeholder="z.B. Basic, Pro, Ultra"
                    required
                  />
                </div>

                <div className="wc-field">
                  <label>Slug (URL-freundlich) *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                    className="wc-input"
                    placeholder="z.B. basic, pro, ultra"
                    required
                  />
                </div>
              </div>

              <div className="wc-field">
                <label>Beschreibung</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="wc-input"
                  placeholder="z.B. Perfekt für kleine Werkstätten"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>Preis Monatlich (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price_monthly}
                    onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                    className="wc-input"
                    placeholder="29.90"
                    required
                  />
                </div>

                <div className="wc-field">
                  <label>Preis Jährlich (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price_yearly}
                    onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                    className="wc-input"
                    placeholder="299.00"
                    required
                  />
                </div>
              </div>

              <div className="wc-field">
                <label>Features (JSON) - für technische Limits</label>
                <textarea
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  className="wc-input wc-mono"
                  rows={3}
                  placeholder='{"admins": 1, "users": 3, "customers": 250}'
                />
                <p className="wc-muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
                  JSON-Objekt mit technischen Limits (für API/Integration)
                </p>
              </div>

              <div className="wc-field">
                <label>Feature-Liste - für Marketing & Website</label>
                <FeatureListEditor
                  value={formData.feature_list}
                  onChange={(value) => setFormData({ ...formData, feature_list: value })}
                />
                <p className="wc-muted" style={{ fontSize: 11, margin: '8px 0 0' }}>
                  Diese Features werden auf der App-Details-Seite und im Checkout mit Checkmarks angezeigt
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>Sortierung</label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label className="wc-check">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Aktiv
                  </label>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label className="wc-check">
                    <input
                      type="checkbox"
                      checked={formData.is_popular}
                      onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                    />
                    Meistgewählt
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setShowModal(false)} className="wc-btn wc-btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="wc-btn wc-btn-primary">
                  {editingPlan ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
